/* eslint-disable max-lines */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import ArrowRightIcon from '@assets/images/arrow-right-icon.svg?react'
import FolderIcon from '@assets/images/folder-icon.svg?react'

import { PopUp } from '@components/PopUp'
import { FileIcon } from '@components/FileIcon'

import { ProjectFile, ProjectFolder, ProjectFolderRole } from '@types'

import { getFileUrl } from '@utils/getFileUrl'
import { getFileTypeFromName } from '@utils/string'

import { useProjectDocuments } from '@hooks/project/project-documents.hook'
import { useProjectGuards } from '@hooks/project/project-guards.hook'

import { CsvViewer } from '../CsvViewer'
import {
  Container,
  HeaderContainer,
  HeaderFolderContainer,
  HeaderLabel,
  ListContainter,
  RowContainer,
  RowIconContainer,
  RowInfoContainer,
  RowLabelContainer,
  ScrollContainer,
} from './PopUpImportResponses.styled'

interface Props {
  projectId: string
  selectedFolder?: ProjectFolder
  selectedFile?: ProjectFile
  onClose: () => void
  onSubmit: (col: string[]) => void
}

export const PopUpImportResponses = (props: Props): JSX.Element => {
  const { projectId, selectedFolder, selectedFile, onClose, onSubmit } = props
  const { t } = useTranslation()

  const [selectedFolders, setSelectedFolders] = useState<ProjectFolder[]>([])
  const [selectedCSVFile, setSelectedCSVFile] = useState<ProjectFile | undefined>(undefined)
  const [csvColSelected, setCsvColSelected] = useState<string[] | undefined>(undefined)
  const [currentScrollIndex, setCurrentScrollIndex] = useState<number>(0)
  const [filesPreview, setFilesPreview] = useState<ProjectFile[]>([])

  const refScrollContainer = useRef<HTMLDivElement | null>(null)

  const { getProjectGuards } = useProjectGuards({ projectId })
  const { canManageFolderAccess } = getProjectGuards()
  const { data: documentsData } = useProjectDocuments(projectId)

  const folders = useMemo(
    () => documentsData?.project?.documentFolders || [],
    [documentsData?.project?.documentFolders],
  )

  const files = useMemo(() => documentsData?.project?.documents || [], [documentsData?.project?.documents])

  const filteredFolders = useMemo(() => {
    if (selectedCSVFile) return []

    let filtered = folders
      .filter((folder) => folder.id !== selectedFolder?.id)
      .sort((a, b) => (a.name < b.name ? -1 : 1))

    if (!canManageFolderAccess) {
      filtered = filtered.filter((folder) => {
        const folderRole = folder?.authorizedRole || folder?.loggedUserRole
        return folderRole === ProjectFolderRole.RoleProjectFolderEdit
      })
    }

    return filtered
  }, [selectedCSVFile, folders, selectedFolder, canManageFolderAccess])

  const navigateToLevel = useCallback(
    (level: 'root' | number | 'file') => {
      switch (level) {
        case 'root':
          setCurrentScrollIndex(0)
          setSelectedFolders([])
          setSelectedCSVFile(undefined)
          setCsvColSelected(undefined)
          break

        case 'file':
          if (selectedCSVFile?.folderId) {
            const parentFolder = folders.find((f) => f.id === selectedCSVFile.folderId)
            if (parentFolder) {
              const parentFolders = getParents(parentFolder, folders)
              setSelectedFolders([...parentFolders, parentFolder])
              setCurrentScrollIndex(parentFolders.length + 1)
            }
          }
          break

        default:
          if (typeof level === 'number' && level >= 0) {
            setSelectedFolders((prev) => prev.slice(0, level + 1))
            setCurrentScrollIndex(level + 1)
            setSelectedCSVFile(undefined)
            setCsvColSelected(undefined)
          }
      }
    },
    [selectedCSVFile, folders],
  )

  const handleFolderClick = useCallback(
    (folder: ProjectFolder) => {
      const index = selectedFolders.findIndex((f) => f.id === folder.id)
      if (index !== -1) {
        setSelectedFolders((prev) => prev.slice(0, index + 1))
        setCurrentScrollIndex(index + 1)
      } else {
        setSelectedFolders((prev) => [...prev, folder])
        setCurrentScrollIndex(selectedFolders.length + 1)
      }
      setSelectedCSVFile(undefined)
      setCsvColSelected(undefined)
    },
    [selectedFolders],
  )

  // Handle file selection
  const handleFileClick = useCallback((file: ProjectFile) => {
    if (getFileTypeFromName(file.file.name) === 'excel') {
      setSelectedCSVFile(file)
      setCsvColSelected(undefined)
    }
  }, [])

  useEffect(() => {
    if (selectedFolder) {
      const defaultSelectedFolders = getParents(selectedFolder, folders)
      setSelectedFolders(defaultSelectedFolders)
      setCurrentScrollIndex(defaultSelectedFolders.length)
    } else if (selectedFile?.folderId) {
      const folder = folders.find((f) => f.id === selectedFile.folderId)
      if (folder) {
        const defaultSelectedFolders = [...getParents(folder, folders), folder]
        setSelectedFolders(defaultSelectedFolders)
        setCurrentScrollIndex(defaultSelectedFolders.length)
      }
    }
  }, [folders, selectedFolder, selectedFile])

  useEffect(() => {
    const loadFilePreview = async () => {
      const formattedFiles: ProjectFile[] = []
      for (const projectFile of files || []) {
        const url = await getFileUrl(projectFile.url)
        formattedFiles.push({ ...projectFile, url: url as any })
      }
      setFilesPreview(formattedFiles.sort((a, b) => (a.name < b.name ? -1 : 1)))
    }

    if (files) {
      loadFilePreview()
    }
  }, [files])

  useEffect(() => {
    if (refScrollContainer.current) {
      const { clientWidth } = refScrollContainer.current
      refScrollContainer.current.scrollTo({
        left: clientWidth * currentScrollIndex,
        behavior: 'auto',
      })
    }
  }, [currentScrollIndex])

  return (
    <PopUp
      style={{ width: 600 }}
      type="validate"
      title={t('modules_project-form-models_components_PopUpImportResponses_title')}
      onSubmitLabel={t('modules_project-form-models_components_PopUpImportResponses_submit_title')}
      onClose={onClose}
      onSubmitDisabled={!csvColSelected}
      onSubmit={() => csvColSelected && onSubmit(csvColSelected)}
    >
      <Container>
        <HeaderContainer>
          <HeaderLabel onClick={() => navigateToLevel('root')}>
            {t('modules_project-form-models_components_PopUpImportResponses_document_label')}
          </HeaderLabel>

          {selectedFolders.map((folder, index) => (
            <HeaderFolderContainer key={folder.id}>
              <ArrowRightIcon />
              <HeaderLabel onClick={() => navigateToLevel(index)}>{folder.name}</HeaderLabel>
            </HeaderFolderContainer>
          ))}

          {selectedCSVFile && (
            <HeaderFolderContainer>
              <ArrowRightIcon />
              <HeaderLabel>{selectedCSVFile.name}</HeaderLabel>
            </HeaderFolderContainer>
          )}
        </HeaderContainer>

        {selectedCSVFile ? (
          <ScrollContainer>
            <CsvViewer file={selectedCSVFile} onColSelected={(col) => setCsvColSelected(col)} />
          </ScrollContainer>
        ) : (
          <ScrollContainer ref={refScrollContainer}>
            <ListContainter>
              {filteredFolders
                .filter((folder) => !folder.parentId)
                .map((folder) => (
                  <RowContainer key={folder.id}>
                    <RowInfoContainer selected={false} onClick={() => handleFolderClick(folder)}>
                      <RowIconContainer>
                        <FolderIcon />
                      </RowIconContainer>
                      <RowLabelContainer>{folder.name}</RowLabelContainer>
                    </RowInfoContainer>
                  </RowContainer>
                ))}

              {filesPreview
                .filter((file) => !file.folderId)
                .map((file) => (
                  <RowContainer
                    key={file.id}
                    className="file"
                    isClickable={getFileTypeFromName(file.file.name) === 'excel'}
                    onClick={() => handleFileClick(file)}
                  >
                    <RowInfoContainer>
                      <RowIconContainer>
                        <FileIcon fileName={file.file.name} />
                      </RowIconContainer>
                      <RowLabelContainer>{file.name}</RowLabelContainer>
                    </RowInfoContainer>
                  </RowContainer>
                ))}
            </ListContainter>

            {selectedFolders.map((parent) => (
              <ListContainter key={`list-${parent.id}`}>
                {getChildren(parent, folders)
                  .filter((folder) => folder.id !== selectedFolder?.id)
                  .map((folder) => (
                    <RowContainer key={folder.id}>
                      <RowInfoContainer selected={false} onClick={() => handleFolderClick(folder)}>
                        <RowIconContainer>
                          <FolderIcon />
                        </RowIconContainer>
                        <RowLabelContainer>{folder.name}</RowLabelContainer>
                      </RowInfoContainer>
                    </RowContainer>
                  ))}

                {filesPreview
                  .filter((file) => file.folderId === parent.id)
                  .map((file) => (
                    <RowContainer
                      key={file.id}
                      className="file"
                      isClickable={getFileTypeFromName(file.file.name) === 'excel'}
                      onClick={() => handleFileClick(file)}
                    >
                      <RowInfoContainer>
                        <RowIconContainer>
                          <FileIcon fileName={file.file.name} />
                        </RowIconContainer>
                        <RowLabelContainer>{file.name}</RowLabelContainer>
                      </RowInfoContainer>
                    </RowContainer>
                  ))}
              </ListContainter>
            ))}
          </ScrollContainer>
        )}
      </Container>
    </PopUp>
  )
}

// Utility functions
const getChildren = (parent: ProjectFolder, folders: ProjectFolder[]) => {
  return folders.filter((folder) => folder.parentId === parent.id)
}

const getParent = (child: ProjectFolder, folders: ProjectFolder[]) => {
  return folders.find((folder) => folder.id === child.parentId)
}

const getParents = (folder: ProjectFolder, folders: ProjectFolder[]): ProjectFolder[] => {
  const parent = getParent(folder, folders)
  if (parent) {
    return [...getParents(parent, folders), parent]
  }
  return []
}
